import { Link } from 'react-router-dom';
import { money } from '../../../lib/api';
import type { ChoreCompletion, Notification, Transaction } from '../../../shared/types';
import type { Resource } from '../useResource';
import { unreadCount } from '../homeData';
import { ErrorState, Icon, Skeleton, type IconName, type Tone } from '../ui';

interface Nudge {
  key: string;
  tone: Exclude<Tone, 'quiet'>;
  icon: IconName;
  title: string;
  body: string;
  to: string;
  cta: string;
}

/**
 * The top of the screen: the two or three things that actually want a human
 * today. Uncategorized transactions come first — an uncategorized transaction is
 * spending the budget can't see, so it's the one thing on this screen that is
 * allowed to shout.
 *
 * Anything with nothing to say renders nothing. When it's all clear, that's worth
 * saying out loud too — but ONLY when every source actually answered. A read that
 * failed is not an all-clear: silence from /chores or /notifications would
 * otherwise be rendered as "the house is in good shape" while a kid's allowance
 * sits unapproved. So any failure is named here, with a retry, and the all-clear
 * is withheld. Sources that did answer still get to speak.
 */
export default function NeedsYou({
  uncategorized,
  approvals,
  notifications,
}: {
  uncategorized: Resource<Transaction[]>;
  approvals: Resource<ChoreCompletion[]>;
  notifications: Resource<Notification[]>;
}) {
  const loading = uncategorized.loading || approvals.loading || notifications.loading;

  const failures = [
    { what: 'transactions', res: uncategorized },
    { what: 'chore approvals', res: approvals },
    { what: 'notifications', res: notifications },
  ].filter((s) => s.res.error !== null);

  const nudges: Nudge[] = [];

  const uncat = uncategorized.data ?? [];
  if (uncat.length > 0) {
    const total = uncat.reduce((sum, t) => sum + t.amount, 0);
    nudges.push({
      key: 'uncategorized',
      tone: 'over',
      icon: 'wallet',
      title: `${uncat.length} transaction${uncat.length === 1 ? '' : 's'} need${
        uncat.length === 1 ? 's' : ''
      } a category`,
      body: `${money(total)} the budget can't see yet. Categorize once and the same merchant fills itself in next time.`,
      to: '/money/transactions',
      cta: 'Categorize them',
    });
  }

  const pending = approvals.data ?? [];
  if (pending.length > 0) {
    const owed = pending.reduce((sum, c) => sum + c.rate_at_time, 0);
    nudges.push({
      key: 'approvals',
      tone: 'near',
      icon: 'broom',
      title: `${pending.length} chore${pending.length === 1 ? '' : 's'} waiting on your approval`,
      body: `${money(owed)} in allowance can't land until you say it's done.`,
      to: '/chores',
      cta: 'Review chores',
    });
  }

  const unread = unreadCount(notifications.data);
  if (unread > 0) {
    nudges.push({
      key: 'notifications',
      tone: 'ok',
      icon: 'bell',
      title: `${unread} unread notification${unread === 1 ? '' : 's'}`,
      body: 'Budget warnings, chores, and meal reminders from the house.',
      to: '/notifications',
      cta: 'Open notifications',
    });
  }

  if (loading) {
    return (
      <div className="rounded-card border border-border bg-surface p-5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-3 h-4 w-full" />
      </div>
    );
  }

  // Something didn't answer. Say what, don't guess — and never claim all-clear.
  const failureBanner = failures.length > 0 && (
    <div className="rounded-card border border-clay/30 bg-clay-soft p-5">
      <ErrorState
        what={joinList(failures.map((f) => f.what))}
        onRetry={() => failures.forEach((f) => f.res.reload())}
      />
      <p className="mt-3 text-sm text-ink-soft">
        Until {failures.length === 1 ? 'it loads' : 'they load'}, this list can&rsquo;t tell you
        everything that needs you.
      </p>
    </div>
  );

  if (nudges.length === 0) {
    return (
      failureBanner || (
        <p className="flex items-center gap-2 rounded-card border border-border bg-surface px-5 py-4 text-sm text-ink-soft">
          <Icon name="check" className="h-5 w-5 text-sage" />
          Nothing needs you right now. The house is in good shape.
        </p>
      )
    );
  }

  const [first, ...rest] = nudges;

  return (
    <div className="space-y-3">
      {failureBanner}
      <NudgeCard nudge={first} big />
      {rest.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {rest.map((n) => (
            <NudgeCard key={n.key} nudge={n} />
          ))}
        </div>
      )}
    </div>
  );
}

/** "transactions" / "transactions and notifications" / "a, b and c" */
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const SURFACE: Record<Exclude<Tone, 'quiet'>, string> = {
  over: 'border-clay/30 bg-clay-soft',
  near: 'border-honey/30 bg-honey-soft',
  ok: 'border-sage/25 bg-sage-soft',
};

const INK: Record<Exclude<Tone, 'quiet'>, string> = {
  over: 'text-clay',
  near: 'text-honey',
  ok: 'text-sage',
};

function NudgeCard({ nudge, big = false }: { nudge: Nudge; big?: boolean }) {
  return (
    <Link
      to={nudge.to}
      className={`group flex items-start gap-4 rounded-card border p-5 transition-shadow hover:shadow-[0_2px_10px_rgba(43,39,35,0.08)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
        SURFACE[nudge.tone]
      }`}
    >
      <span className={`mt-0.5 shrink-0 ${INK[nudge.tone]}`}>
        <Icon name={nudge.icon} className={big ? 'h-7 w-7' : 'h-5 w-5'} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block font-semibold text-ink ${big ? 'font-display text-xl sm:text-2xl' : 'text-base'}`}
        >
          {nudge.title}
        </span>
        <span className="mt-1 block text-sm text-ink-soft">{nudge.body}</span>
        <span
          className={`mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium ${INK[nudge.tone]}`}
        >
          {nudge.cta}
          <Icon name="arrow" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </span>
    </Link>
  );
}
